import React, { ElementType, HTMLAttributes, ReactNode } from 'react';
import AppLink from '../AppLink/AppLink';

export type CardVariant = 'matte';
export type CardColor = 'default' | 'neutral' | 'daily' | 'xp' | 'streak';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg' | 'xl';
export type CardShadow = 'default' | 'none';

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
  variant?: CardVariant;
  color?: CardColor;
  padding?: CardPadding;
  px?: CardPadding;
  py?: CardPadding;
  interactive?: boolean;
  shadow?: CardShadow;
  as?: ElementType;
  to?: string;
  href?: string;
  children: ReactNode;
  className?: string;
}

const PADDING_X_MAP: Record<CardPadding, string> = {
  none: 'px-0',
  sm: 'px-4',
  md: 'px-6',
  lg: 'px-10',
  xl: 'px-12',
};

const PADDING_Y_MAP: Record<CardPadding, string> = {
  none: 'py-0',
  sm: 'py-3',
  md: 'py-4',
  lg: 'py-6',
  xl: 'py-8',
};

const COLOR_MAP: Record<string, string> = {
  neutral: 'bg-neutral',
  daily: 'bg-daily',
  xp: 'bg-xp',
  streak: 'bg-streak',
  default: 'bg-neutral',
};

/**
 * Card Component
 *
 * A polymorphic card component that enforces the Surface Materials architecture.
 * It uses semantic solid background colors.
 *
 * @example
 * <Card color="neutral" interactive onClick={() => {}}>...</Card>
 */
export const Card = ({
  variant,
  color = 'default',
  padding = 'md',
  px,
  py,
  interactive = false,
  shadow = 'default',
  as,
  to,
  href,
  children,
  className = '',
  style,
  ...props
}: CardProps) => {
  const Component = as || (to ? AppLink : href ? 'a' : 'div');
  const paddingXClass = PADDING_X_MAP[px || padding];
  const paddingYClass = PADDING_Y_MAP[py || padding];
  const baseClasses = `relative flex flex-col rounded-xl overflow-hidden transition-all duration-fast ${paddingXClass} ${paddingYClass}`;

  const interactionClasses =
    interactive || to || href
      ? 'cursor-pointer dark:hover:brightness-105 active:scale-[0.99] hover:shadow-default-hover'
      : '';

  // Simplified architecture: Solid Colors + Border + Shadow
  let colorClasses = COLOR_MAP[color as string] || COLOR_MAP.default;
  let materialClasses = shadow === 'none' ? 'shadow-none' : 'shadow-default';
  const customStyle = { ...style };

  // Explicit variant overrides
  if (variant === 'matte') {
    // Legacy support for 'matte' (now just subtle bg, no border/shadow)
    colorClasses = 'bg-subtle border-none';
    materialClasses = 'shadow-none';
  }

  const mergedClasses = [baseClasses, colorClasses, materialClasses, interactionClasses, className]
    .filter(Boolean)
    .join(' ');

  const linkProps = to ? { to } : {};
  const anchorProps = href ? { href } : {};

  return (
    <Component
      className={mergedClasses}
      style={customStyle}
      {...linkProps}
      {...anchorProps}
      {...props}
    >
      {children}
    </Component>
  );
};
